import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt, faSpinner, faTimes, faUser } from "@fortawesome/free-solid-svg-icons";
import Tooltip from "@mui/material/Tooltip";
import { faMoon, faSun } from "@fortawesome/free-solid-svg-icons";

import { setThemePreference } from "../../state/reducers/themeReducer";
import { RootState } from '../../state/store';
import { logoutUser } from '../../state/actions/authActions';
import { useAppDispatch } from '../../state/hooks';
import { hasAllRoles, hasAnyRole } from '../../utilities/helpers/roleUtilities';
import { UserDto } from '../../DTOs/User/UserDto';
import { AppRouteConfig, createAppRoutes } from "../../utilities/appRoutes";
import { useGlobalModal } from "../Contexts/GlobalModalContext";
import camelToCapitalized from '../../utilities/helpers/camelCaseToCapitalized';

interface MenuItem {
  label: string;
  to?: string;
  onClick?: () => void; // instead of linking
  subItems?: MenuItem[];
}

// Get a label from a route path
const pathToLabel = (path: string) =>
  path
    .split('/')
    .filter(Boolean)
    .map(s => s.replace(/-/g, ' '))
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' > ');

// Check user has permission for a route
const isRouteAllowed = (route: AppRouteConfig, user: UserDto | null) => {
  if (user && hasAnyRole(user, "SuperAdmin")) return true;
  if (!route.protected) return true;
  if (!user) return false;
  if (!route.access && !route.accessAll) return true;
  const anyRoleAllowed = !route.access || hasAnyRole(user, route.access);
  const allRolesRequired = !route.accessAll || hasAllRoles(user, route.accessAll);
  return anyRoleAllowed && allRolesRequired;
};

const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isAuthLoading, user, roleConfig } = useSelector((s: RootState) => s.authReducer);
  const currentView = useSelector((s: RootState) => s.view.currentView);
  const themePref = useSelector((s: RootState) => s.theme.preference);
  const { showModal } = useGlobalModal();

  const [menuOpen, setMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const openMenu = () => setMenuOpen(true);

  const closeMenu = () => {
    menuButtonRef.current?.focus();
    setMenuOpen(false);
  };

  const toggleMenu = () => {
    if (menuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const toggleTheme = () => {
    dispatch(setThemePreference(themePref === "dark" ? "light" : "dark"));
  };

  const themeIcon = themePref === "dark" ? faMoon : faSun;
  const themeLabel = themePref === "dark" ? "Theme: Dark" : "Theme: Light";

  const toggleCategory = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(logoutUser());
    closeMenu();
  };

  // Recursively convert route config to menu structure
  const buildMenu = (routes: AppRouteConfig[], user: UserDto | null, parentPath = ''): MenuItem[] => {
    const menu: MenuItem[] = [];

    for (const route of routes) {
      if (!route.path || route.path === '*' || route.ignore) continue;
      if (!isRouteAllowed(route, user)) continue;

      const fullPath = `${parentPath}/${route.path}`.replace(/\/+/g, '/'); // Normalize slashes
      const hasChildren = route.children && route.children.length > 0;
      const subItems = hasChildren ? buildMenu(route.children!, user, fullPath) : [];

      const isGroupOnly = !route.element && subItems.length > 0;

      // Skip routes without elements and without children
      if (!route.element && !route.action && !hasChildren) continue;

      const menuItem: MenuItem = {
        label: route.label ?? pathToLabel(route.path),
        ...(route.action
          ? {
            onClick: () => route.action!(showModal)
          }
          : isGroupOnly ? {} : { to: fullPath }),
        ...(subItems.length > 0 ? { subItems } : {})
      };

      menu.push(menuItem);
    }

    return menu;
  };

  const routes = useMemo(() => roleConfig ? createAppRoutes(roleConfig) : [], [roleConfig]);
  const menuItems = buildMenu(routes, user);

  const renderMenuItems = (items: MenuItem[], depth = 0) => {
    return (
      <ul className={`${depth > 0 ? "pl-3 py-2 mt-1 mb-2 border-l border-theme-border ml-2" : ""}`}>
        {items.map(item => {
          const itemKey = `${depth}-${item.label}`;

          return (
            <li key={itemKey} className={`${depth > 0 ? "" : "border-t border-theme-border first:border-t-0"}`}>
              {item.subItems ? (
                <>
                  <button
                    type="button"
                    onClick={() => toggleCategory(itemKey)}
                    className="w-full flex justify-between items-center px-3 py-2 cursor-pointer hover:bg-theme-primary-hover text-left text-theme-primary-contrast transition"
                  >
                    <span>{item.label}</span>
                    <span className="ml-3 shrink-0">{expanded.has(itemKey) ? "▾" : "▸"}</span>
                  </button>
                  {expanded.has(itemKey) && renderMenuItems(item.subItems, depth + 1)}
                </>
              ) : item.onClick ? (
                <button
                  onClick={() => {
                    item.onClick?.();
                    closeMenu();
                  }}
                  className="block w-full text-left px-3 py-2 hover:bg-theme-primary-hover text-theme-primary-contrast transition"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  to={item.to!}
                  onClick={closeMenu}
                  className="block px-3 py-2 hover:bg-theme-primary-hover text-theme-primary-contrast transition"
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const currentViewDisplay = camelToCapitalized(currentView || '');

  useEffect(() => {
    if (!menuOpen) return;
    closeButtonRef.current?.focus();

    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = original;
    };
  }, [menuOpen]);

  return (
    <>
      <header className="bg-theme-primary text-theme-primary-contrast border-b border-theme-border relative transition duration-150 sticky top-0 z-30 overflow-hidden">
        <nav className="flex items-center justify-between px-4 py-3 short:py-1">
          <div className="flex items-center gap-2 md:gap-4">
            <button
              ref={menuButtonRef}
              onClick={toggleMenu}
              aria-label="Toggle menu"
              className="flex flex-col justify-center items-center gap-1 w-8 h-8 short:w-6 short:h-6 p-1 mr-2 cursor-pointer"
            >
              <span className="block w-full h-1 short:h-0.5 bg-theme-primary-contrast rounded" />
              <span className="block w-full h-1 short:h-0.5 bg-theme-primary-contrast rounded" />
              <span className="block w-full h-1 short:h-0.5 bg-theme-primary-contrast rounded" />
            </button>
            <h2 className="font-semibold text-sm short:text-[0.8rem] md:text-lg">{currentViewDisplay}</h2>
          </div>

          <div className="flex-none space-x-4 flex gap-2 items-center transition">
            <Tooltip title={themeLabel} arrow>
              <button
                type="button"
                onClick={toggleTheme}
                className="flex items-center justify-center transition duration-150 hover:text-theme-primary-contrast-hover cursor-pointer"
                aria-label={themeLabel}
              >
                <FontAwesomeIcon icon={themeIcon} className="!w-6 !h-6 short:!w-4 short:!h-4 sm:!w-5 sm:!h-5" />
              </button>
            </Tooltip>

            {isAuthLoading ? (
              <span className={`transition opacity-${isAuthLoading ? "100" : "0"}`}>
                <FontAwesomeIcon icon={faSpinner} spin />
              </span>
            ) : (
              isAuthenticated && (
                <Link
                  to="/account"
                  className={`font-semibold hover:text-theme-primary-contrast-hover transition duration-150 opacity-${isAuthLoading ? "0" : "100"}`}
                >
                  <span className="!hidden sm:!inline short:text-[0.8rem]">{user?.name ?? "User"}</span>
                  <FontAwesomeIcon icon={faUser} className="!block sm:!hidden !w-6 !h-6 short:!w-4 short:!h-4" />
                </Link>
              )
            )}

            {isAuthenticated ? (
              <button onClick={handleLogout} className="transition duration-150 hover:text-theme-primary-contrast-hover cursor-pointer">
                <span className="!hidden sm:!inline short:text-[0.8rem]">Logout</span>
                <FontAwesomeIcon icon={faSignOutAlt} className="!block sm:!hidden !w-6 !h-6 short:!w-4 short:!h-4" />
              </button>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)} className="transition duration-150 hover:text-theme-primary-contrast-hover">
                Login
              </Link>
            )}
          </div>
        </nav>
      </header>

      <div
        className={`fixed inset-0 z-50 transition duration-150 ${menuOpen ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-150 ${menuOpen ? "opacity-100" : "opacity-0"}`}
          onClick={closeMenu}
        />

        <aside
          className={`absolute top-0 left-0 h-full w-[22rem] max-w-[90vw] bg-theme-primary text-theme-primary-contrast border-theme-border transition-transform duration-150 flex flex-col ${menuOpen ? "translate-x-0 border-r shadow-2xl shadow-theme-border" : "-translate-x-full"}`}
        >
          <div className="flex items-center justify-between px-4 py-4 short:py-1 border-b border-theme-primary-contrast shrink-0">
            <button
              ref={closeButtonRef}
              onClick={closeMenu}
              className="text-3xl short:text-2xl leading-none hover:text-theme-primary-contrast-hover transition cursor-pointer"
              aria-label="Close menu"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h3 className="font-semibold text-lg short:text-[0.8rem]">Menu</h3>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain">
            {renderMenuItems(menuItems)}
          </div>
        </aside>
      </div>
    </>
  );
};

export default Header;
